import { useMemo } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { ArrowRight, ClipboardCheck, FileEdit, FolderKanban, CalendarDays, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { moduleForPath } from "@/components/layout/navConfig";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate } from "@/lib/format";

/* My Workspace: the person's own work (approvals waiting, drafts, recent documents). Project progress and health live in the
   Portfolio overview, so they are not repeated here. */
const money = (v) => (v === null || v === undefined ? "" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

function Card({ title, note, right, children, className = "", testId }) {
  return (
    <section className={`min-w-0 rounded-2xl border border-border bg-card p-4 shadow-sm ${className}`} data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}
const Empty = ({ text }) => <p className="px-2 py-8 text-center text-sm text-muted-foreground">{text}</p>;

/* pastel KPI card: tone = light pastel + dark equivalents */
const TONES = {
  rose:  "bg-[#FBE9EE] dark:bg-[#3A1F29] text-[#910029] dark:text-[#E5849F]",
  blue:  "bg-[#E6F0FD] dark:bg-[#1E2C42] text-[#2A62B8] dark:text-[#8FB6F0]",
  amber: "bg-[#FDF3DC] dark:bg-[#3A3018] text-[#9A6A00] dark:text-[#E8BE5A]",
  green: "bg-[#E3F6EE] dark:bg-[#17332A] text-[#0E7C57] dark:text-[#6FD1AA]",
};
function Kpi({ tone, icon: Icon, label, value, to, hint, loading }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-4 ${TONES[tone]}`} data-testid={`workspace-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold opacity-80">{label}</p>
          <p className="mt-1 text-3xl font-bold tabular-nums">{loading ? "–" : value}</p>
          {hint && <p className="mt-0.5 text-[11px] opacity-70">{hint}</p>}
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white/60 dark:bg-white/10"><Icon className="h-5 w-5" /></span>
      </div>
      {to && <Link to={to} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold hover:underline">View Details <ArrowRight className="h-3 w-3" /></Link>}
    </div>
  );
}


export default function WorkspaceDashboard({ user, loading, awaiting = [], drafts = [], draftTotal = 0, recent = [], quick = [] }) {
  const { canView, loading: permLoading } = usePermissions();
  const { user: me } = useAuth();
  const canSeePortfolio = !permLoading && canView(moduleForPath("/erp/delivery/all-projects"));
  const who = user || me;
  const first = (who?.full_name || "").split(" ")[0];

  const pendingByType = useMemo(() => {
    const m = new Map();
    awaiting.forEach((d) => { const e = m.get(d.source.key) || { s: d.source, n: 0 }; e.n += 1; m.set(d.source.key, e); });
    return [...m.values()];
  }, [awaiting]);

  return (
    <div className="space-y-5" data-testid="my-workspace-page">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <nav className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground">Home</Link><span>›</span><span className="font-medium text-foreground">My Workspace</span>
          </nav>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Workspace</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Welcome back{first ? `, ${who.full_name}` : ""}! Here&apos;s what needs your attention today.</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-foreground shadow-sm">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />{dayjs().format("dddd, D MMM YYYY")}
        </span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="workspace-summary">
        <Kpi tone="blue" icon={ClipboardCheck} label="Awaiting approval" value={awaiting.length} hint="Documents waiting for a decision" loading={loading} />
        <Kpi tone="amber" icon={FileEdit} label="Drafts to complete" value={draftTotal} hint="Saved but not yet submitted" loading={loading} />
      </div>

      {canSeePortfolio && (
        <Link to="/" className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-sm shadow-sm hover:bg-accent/40" data-testid="workspace-portfolio-link">
          <span className="flex min-w-0 items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-secondary"><FolderKanban className="h-4 w-4 text-muted-foreground" /></span>
            <span className="min-w-0"><span className="block font-semibold text-foreground">Project progress and health</span><span className="block text-xs text-muted-foreground">Completion, delays, alerts and the S-curve for every project are on the Portfolio overview.</span></span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">Open Portfolio <ArrowRight className="h-3 w-3" /></span>
        </Link>
      )}

      {/* pending approval + side lists */}
      <div className="grid items-start gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Pending Approval" testId="workspace-approvals"
          right={!loading && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{awaiting.length}</span>}>
          {loading ? <Empty text="Loading…" /> : awaiting.length === 0 ? <Empty text="Nothing is waiting for approval." /> : (
            <div className="grid gap-4 md:grid-cols-[1fr_200px]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-muted-foreground"><th className="py-2 text-left font-medium">Document</th><th className="px-2 py-2 text-left font-medium">Number</th><th className="px-2 py-2 text-left font-medium">Date</th><th className="px-2 py-2 text-right font-medium">Amount</th><th className="py-2 pl-2 text-left font-medium">Status</th></tr></thead>
                  <tbody>
                    {awaiting.slice(0, 8).map((d) => (
                      <tr key={d.id} className="border-t border-border hover:bg-accent/40">
                        <td className="py-2"><Link to={d.source.route} className="font-medium text-foreground hover:text-[#910029] hover:underline">{d.source.label}</Link></td>
                        <td className="px-2 py-2 font-mono text-xs">{d.number}</td>
                        <td className="px-2 py-2 text-muted-foreground">{d.date ? fmtDate(d.date) : "—"}</td>
                        <td className="px-2 py-2 text-right font-mono text-xs">{money(d.amount)}</td>
                        <td className="py-2 pl-2"><StatusPill value={d.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {awaiting.length > 8 && <p className="border-t border-border pt-2 text-xs text-muted-foreground">Showing the latest 8 of {awaiting.length}. Open the document list to see the rest.</p>}
              </div>
              <ul className="space-y-2 md:border-l md:border-border md:pl-4">
                {pendingByType.map(({ s, n }) => (
                  <li key={s.key}>
                    <Link to={s.route} className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2 text-xs font-medium hover:bg-secondary">
                      <span className="truncate">{s.label}</span><span className="ml-2 rounded-full bg-card px-2 py-0.5 tabular-nums">{n}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card title="Drafts to complete" testId="workspace-drafts" right={!loading && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{draftTotal}</span>}>
            {loading ? <Empty text="Loading…" /> : drafts.length === 0 ? <Empty text="No drafts open." /> : (
              <ul className="divide-y divide-border">
                {drafts.map(({ s, n }) => (
                  <li key={s.key}><Link to={s.route} className="flex items-center justify-between py-2 text-sm hover:text-[#910029]"><span>{s.label}</span><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium tabular-nums">{n}</span></Link></li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Recent documents" testId="workspace-recent" right={!loading && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{recent.length}</span>}>
          {loading ? <Empty text="Loading…" /> : recent.length === 0 ? <Empty text="No documents yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground"><th className="py-2 text-left font-medium">Document</th><th className="px-2 py-2 text-left font-medium">Number</th><th className="px-2 py-2 text-left font-medium">Date</th><th className="px-2 py-2 text-right font-medium">Amount</th><th className="py-2 pl-2 text-left font-medium">Status</th></tr></thead>
                <tbody>
                  {recent.map((d) => (
                    <tr key={d.id} className="border-t border-border hover:bg-accent/40">
                      <td className="py-2"><Link to={d.source.route} className="font-medium text-foreground hover:text-[#910029] hover:underline">{d.source.label}</Link></td>
                      <td className="px-2 py-2 font-mono text-xs">{d.number}</td>
                      <td className="px-2 py-2 text-muted-foreground">{d.date ? fmtDate(d.date) : "—"}</td>
                      <td className="px-2 py-2 text-right font-mono text-xs">{money(d.amount)}</td>
                      <td className="py-2 pl-2"><StatusPill value={d.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="Quick links" testId="workspace-quick" right={<Zap className="h-4 w-4 text-muted-foreground" />}>
          <div className="grid grid-cols-2 gap-2">
            {quick.map((q) => <Link key={q.to} to={q.to} className="rounded-lg bg-secondary/60 px-3 py-2 text-xs font-medium text-foreground hover:bg-secondary hover:text-[#910029]">{q.label}</Link>)}
          </div>
        </Card>
      </div>
    </div>
  );
}
